import PropTypes from 'prop-types';
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { toast } from "react-toastify";
import axios from 'axios';
import moment from 'moment';
import {
  Card,
  CardBody,
  CardTitle,
  Row,
  Col,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
} from "reactstrap";

//import components
import Breadcrumbs from '../../components/Common/Breadcrumb';

const NewDriverRegistrationDetails = props => {
  const { driverId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [driverInfo, setDriverInfo] = useState(null);
  const [driverDocuments, setDriverDocuments] = useState([]);
  const [vehicleDocuments, setVehicleDocuments] = useState([]);
  
  const [driverModalOpen, setDriverModalOpen] = useState(false);
  const [vehicleModalOpen, setVehicleModalOpen] = useState(false);
  const [selectedDriverDocIds, setSelectedDriverDocIds] = useState([]);
  const [selectedVehicleDocIds, setSelectedVehicleDocIds] = useState([]);
  const [remarks, setRemarks] = useState({});
  const [selectedVehicleGroup, setSelectedVehicleGroup] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [showImagePreview, setShowImagePreview] = useState(false);

  useEffect(() => {
    fetchDriverDetails();
  }, [driverId]);

  const openImagePreview = (imageUrl) => {
    setPreviewImage(imageUrl);
    setShowImagePreview(true);
  };

  const closeImagePreview = () => {
    setPreviewImage(null);
    setShowImagePreview(false);
  };

  const getImageUrl = (doc) => {
    // Le champ 'document' contient déjà l'URL complète depuis WebDriverDocumentResources/WebVehicleDocumentResources
    if (doc.document && doc.document !== '-') return doc.document;
    // Fallback sur les autres chemins possibles
    if (doc.document_media_path && doc.document_media_path !== '-') {
      if (doc.document_media_path.startsWith('http')) {
        return doc.document_media_path;
      }
      return `${process.env.REACT_APP_WEB_APP_URL}/${doc.document_media_path}`;
    }
    if (doc.document_media?.src) {
      if (doc.document_media.src.startsWith('http')) {
        return doc.document_media.src;
      }
      return `${process.env.REACT_APP_WEB_APP_URL}/${doc.document_media.src}`;
    }
    return '';
  };

  const fetchDriverDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${process.env.REACT_APP_WEB_APP_URL}/api/web/new-driver-registration-details/${driverId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success && response.data.data) {
        setDriverInfo(response.data.data.driver_info);
        setDriverDocuments(response.data.data.driver_documents || []);
        setVehicleDocuments(response.data.data.vehicle_documents || []);
      } else {
        toast.error('Failed to fetch driver details', {
          position: "top-right",
          autoClose: 3000,
        });
        navigate('/new-driver-registrations');
      }
    } catch (error) {
      console.error("Error fetching driver details:", error);
      toast.error('Failed to fetch driver details', {
        position: "top-right",
        autoClose: 3000,
      });
      navigate('/new-driver-registrations');
    } finally {
      setLoading(false);
    }
  };

  const toggleDriverDocSelection = (docId) => {
    setSelectedDriverDocIds(prev =>
      prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]
    );
  };

  const toggleVehicleDocSelection = (docId) => {
    setSelectedVehicleDocIds(prev =>
      prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]
    );
  };

  const handleRemarkChange = (docId, value) => {
    setRemarks(prev => ({
      ...prev,
      [docId]: value
    }));
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "approved": return "success";
      case "rejected": return "danger";
      case "pending": return "warning";
      default: return "secondary";
    }
  };

  const isImage = (url) => {
    return /\.(jpeg|jpg|png|gif|bmp|webp)$/i.test(url);
  };

  const getFileExtension = (url) => {
    return url?.split('.').pop() || 'file';
  };

  const handleViewDriverDocuments = () => {
    setSelectedDriverDocIds([]);
    setDriverModalOpen(true);
  };

  const handleViewVehicleDocuments = (vehicleGroup) => {
    setSelectedVehicleGroup(vehicleGroup);
    setSelectedVehicleDocIds([]);
    setVehicleModalOpen(true);
  };

  const handleApproveDriverDocuments = async () => {
    if (selectedDriverDocIds.length === 0) {
      toast.error('Please select at least one document.', {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    const selectedRemarks = selectedDriverDocIds.map(id => ({
      document_id: id,
      remark: remarks[id] || ""
    }));

    const payload = {
      action: 'approved',
      document_ids: selectedRemarks,
      driver_id: driverId
    };

    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        `${process.env.REACT_APP_WEB_APP_URL}/api/web/update-drivers-documents`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "Authorization": `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        toast.success('Driver documents approved successfully', {
          position: "top-right",
          autoClose: 3000,
        });
        setDriverModalOpen(false);
        fetchDriverDetails();
      } else {
        toast.error('Failed to approve documents', {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error("Error approving driver documents:", error);
      toast.error('Failed to approve documents', {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const handleApproveVehicleDocuments = async () => {
    if (selectedVehicleDocIds.length === 0) {
      toast.error('Please select at least one document.', {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    if (!selectedVehicleGroup) {
      toast.error('Vehicle group not selected', {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    const selectedRemarks = selectedVehicleDocIds.map(id => ({
      document_id: id,
      remark: remarks[id] || ""
    }));

    const payload = {
      action: 'approved',
      document_ids: selectedRemarks,
      userId: driverId,
      vehicle_id: selectedVehicleGroup.vehicle_id
    };

    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        `${process.env.REACT_APP_WEB_APP_URL}/api/web/update-vehicles-documents`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "Authorization": `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        toast.success('Vehicle documents approved successfully', {
          position: "top-right",
          autoClose: 3000,
        });
        setVehicleModalOpen(false);
        fetchDriverDetails();
      } else {
        toast.error('Failed to approve documents', {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error("Error approving vehicle documents:", error);
      toast.error('Failed to approve documents', {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  document.title = "New Driver Registration Details | Moeen";
  
  if (loading) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Home" breadcrumbItem="New Driver Registration Details" />
          <Skeleton count={10} height={40} />
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Home" breadcrumbItem="New Driver Registration Details" />
        
        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                <CardTitle className="h4">Driver Information</CardTitle>
                <Row className="mt-3">
                  <Col md="6">
                    <p><strong>Name:</strong> {driverInfo?.name || 'N/A'}</p>
                    <p><strong>Email:</strong> {driverInfo?.email || 'N/A'}</p>
                    <p><strong>Phone:</strong> {driverInfo?.phone_code || ''} {driverInfo?.phone || 'N/A'}</p>
                    <p><strong>First Name:</strong> {driverInfo?.first_name || 'N/A'}</p>
                    <p><strong>Last Name:</strong> {driverInfo?.last_name || 'N/A'}</p>
                  </Col>
                  <Col md="6">
                    {driverInfo?.gender && driverInfo?.gender !== 'N/A' && (
                      <p><strong>Gender:</strong> {driverInfo?.gender}</p>
                    )}
                    {driverInfo?.address && driverInfo?.address !== 'N/A' && (
                      <p><strong>Address:</strong> {driverInfo?.address}</p>
                    )}
                    <p><strong>Status:</strong> 
                      <Badge className={`ms-2 bg-${driverInfo?.is_active ? 'success' : 'danger'}`}>
                        {driverInfo?.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </p>
                    <p><strong>Registration Date:</strong> {moment(driverInfo?.created_at).format('YYYY-MM-DD HH:mm')}</p>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row className="mt-4">
          <Col lg="6">
            <Card>
              <CardBody>
                <CardTitle className="h4">Driver Documents</CardTitle>
                <p className="text-muted">Pending documents: {driverDocuments.length}</p>
                {driverDocuments.length > 0 ? (
                  <Button color="primary" onClick={handleViewDriverDocuments}>
                    <i className="mdi mdi-eye"></i> View & Approve Documents
                  </Button>
                ) : (
                  <p className="text-success">All driver documents are approved</p>
                )}
              </CardBody>
            </Card>
          </Col>
          <Col lg="6">
            <Card>
              <CardBody>
                <CardTitle className="h4">Vehicle Documents</CardTitle>
                <p className="text-muted">Pending vehicles: {vehicleDocuments.length}</p>
                {vehicleDocuments.length > 0 ? (
                  <div>
                    {vehicleDocuments.map((vehicleGroup, index) => (
                      <Button 
                        key={index}
                        color="primary" 
                        className="me-2 mb-2"
                        onClick={() => handleViewVehicleDocuments(vehicleGroup)}
                      >
                        <i className="mdi mdi-eye"></i> View Vehicle {vehicleGroup.vehicle_license_plate || vehicleGroup.vehicle_id}
                      </Button>
                    ))}
                  </div>
                ) : (
                  <p className="text-success">All vehicle documents are approved</p>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Driver Documents Modal */}
        {driverModalOpen && (
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ background: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Driver Documents</h5>
                  <button type="button" className="btn-close" onClick={() => setDriverModalOpen(false)}></button>
                </div>
                <div className="modal-body">
                  <div className="d-flex flex-wrap gap-3">
                    {driverDocuments.map(doc => {
                      const isApproved = doc.status?.toLowerCase() === 'approved';
                      const isSelected = selectedDriverDocIds.includes(doc.id);
                      const docUrl = getImageUrl(doc);

                      return (
                        <div
                          key={doc.id}
                          className={`card p-2 ${isSelected ? 'border border-success' : ''} ${isApproved ? 'opacity-50' : ''}`}
                          style={{ width: "180px", position: "relative" }}
                        >
                          {isSelected && (
                            <div className="position-absolute top-0 end-0 m-1" style={{ zIndex: 10 }}>
                              <span className="badge bg-success">Selected</span>
                            </div>
                          )}
                          <Badge className={`mb-2 bg-${getStatusColor(doc.status)}`} style={{ zIndex: 10, position: "relative" }}>
                            {doc.status}
                          </Badge>
                          {docUrl && (
                            <div className="text-center" style={{ cursor: isImage(docUrl) ? 'pointer' : 'default' }}>
                              {isImage(docUrl) ? (
                                <img 
                                  src={docUrl} 
                                  alt="Document" 
                                  style={{ width: "100%", height: "120px", objectFit: "cover", borderRadius: "4px" }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openImagePreview(docUrl);
                                  }}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <div 
                                className="d-flex align-items-center justify-content-center" 
                                style={{ 
                                  height: "120px", 
                                  background: "#f0f0f0",
                                  borderRadius: "4px",
                                  display: isImage(docUrl) ? 'none' : 'flex'
                                }}
                              >
                                <i className="mdi mdi-file-document" style={{ fontSize: "48px" }}></i>
                              </div>
                            </div>
                          )}
                          {!docUrl && (
                            <div className="d-flex align-items-center justify-content-center" style={{ height: "120px", background: "#f0f0f0", borderRadius: "4px" }}>
                              <i className="mdi mdi-file-document" style={{ fontSize: "48px" }}></i>
                            </div>
                          )}
                          <div className="mt-2">
                            <small className="text-muted">{doc.document_permission?.document_types?.name || 'Document'}</small>
                          </div>
                          <div className="mt-2">
                            <Button
                              size="sm"
                              color={isSelected ? "danger" : "primary"}
                              style={{ width: "100%" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!isApproved) {
                                  toggleDriverDocSelection(doc.id);
                                }
                              }}
                              disabled={isApproved}
                            >
                              {isSelected ? "Deselect" : "Select"}
                            </Button>
                          </div>
                          {isSelected && !isApproved && (
                            <div className="mt-2">
                              <Input
                                type="textarea"
                                placeholder="Remarks (optional)"
                                value={remarks[doc.id] || ''}
                                onChange={(e) => handleRemarkChange(doc.id, e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                rows="2"
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="modal-footer">
                  <Button color="secondary" onClick={() => setDriverModalOpen(false)}>Close</Button>
                  <Button color="success" onClick={handleApproveDriverDocuments} disabled={selectedDriverDocIds.length === 0}>
                    Approve Selected ({selectedDriverDocIds.length})
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Vehicle Documents Modal */}
        {vehicleModalOpen && selectedVehicleGroup && (
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ background: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Vehicle Documents - {selectedVehicleGroup.vehicle_license_plate || selectedVehicleGroup.vehicle_id}</h5>
                  <button type="button" className="btn-close" onClick={() => setVehicleModalOpen(false)}></button>
                </div>
                <div className="modal-body">
                  <div className="d-flex flex-wrap gap-3">
                    {selectedVehicleGroup.documents.map(doc => {
                      const isApproved = doc.status?.toLowerCase() === 'approved';
                      const isSelected = selectedVehicleDocIds.includes(doc.id);
                      const docUrl = getImageUrl(doc);

                      return (
                        <div
                          key={doc.id}
                          className={`card p-2 ${isSelected ? 'border border-success' : ''} ${isApproved ? 'opacity-50' : ''}`}
                          style={{ width: "180px", position: "relative" }}
                        >
                          {isSelected && (
                            <div className="position-absolute top-0 end-0 m-1" style={{ zIndex: 10 }}>
                              <span className="badge bg-success">Selected</span>
                            </div>
                          )}
                          <Badge className={`mb-2 bg-${getStatusColor(doc.status)}`} style={{ zIndex: 10, position: "relative" }}>
                            {doc.status}
                          </Badge>
                          {docUrl && (
                            <div className="text-center" style={{ cursor: isImage(docUrl) ? 'pointer' : 'default' }}>
                              {isImage(docUrl) ? (
                                <img 
                                  src={docUrl} 
                                  alt="Document" 
                                  style={{ width: "100%", height: "120px", objectFit: "cover", borderRadius: "4px" }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openImagePreview(docUrl);
                                  }}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <div 
                                className="d-flex align-items-center justify-content-center" 
                                style={{ 
                                  height: "120px", 
                                  background: "#f0f0f0",
                                  borderRadius: "4px",
                                  display: isImage(docUrl) ? 'none' : 'flex'
                                }}
                              >
                                <i className="mdi mdi-file-document" style={{ fontSize: "48px" }}></i>
                              </div>
                            </div>
                          )}
                          {!docUrl && (
                            <div className="d-flex align-items-center justify-content-center" style={{ height: "120px", background: "#f0f0f0", borderRadius: "4px" }}>
                              <i className="mdi mdi-file-document" style={{ fontSize: "48px" }}></i>
                            </div>
                          )}
                          <div className="mt-2">
                            <small className="text-muted">{doc.document_type?.document_type_name || 'Document'}</small>
                          </div>
                          <div className="mt-2">
                            <Button
                              size="sm"
                              color={isSelected ? "danger" : "primary"}
                              style={{ width: "100%" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!isApproved) {
                                  toggleVehicleDocSelection(doc.id);
                                }
                              }}
                              disabled={isApproved}
                            >
                              {isSelected ? "Deselect" : "Select"}
                            </Button>
                          </div>
                          {isSelected && !isApproved && (
                            <div className="mt-2">
                              <Input
                                type="textarea"
                                placeholder="Remarks (optional)"
                                value={remarks[doc.id] || ''}
                                onChange={(e) => handleRemarkChange(doc.id, e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                rows="2"
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="modal-footer">
                  <Button color="secondary" onClick={() => setVehicleModalOpen(false)}>Close</Button>
                  <Button color="success" onClick={handleApproveVehicleDocuments} disabled={selectedVehicleDocIds.length === 0}>
                    Approve Selected ({selectedVehicleDocIds.length})
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Image Preview Modal */}
        {showImagePreview && previewImage && (
          <div 
            className="modal fade show d-block" 
            tabIndex="-1" 
            role="dialog" 
            style={{ background: "rgba(0,0,0,0.9)", zIndex: 9999 }}
            onClick={closeImagePreview}
          >
            <div className="modal-dialog modal-xl" role="document" style={{ maxWidth: "90vw", margin: "auto" }}>
              <div className="modal-content" style={{ background: "transparent", border: "none" }}>
                <div className="modal-header" style={{ border: "none", justifyContent: "flex-end" }}>
                  <button 
                    type="button" 
                    className="btn-close btn-close-white" 
                    onClick={closeImagePreview}
                    style={{ fontSize: "2rem" }}
                  ></button>
                </div>
                <div className="modal-body d-flex align-items-center justify-content-center" style={{ minHeight: "80vh" }}>
                  <img 
                    src={previewImage} 
                    alt="Preview" 
                    style={{ 
                      maxWidth: "100%", 
                      maxHeight: "80vh", 
                      objectFit: "contain",
                      borderRadius: "8px"
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

NewDriverRegistrationDetails.propTypes = {
  t: PropTypes.any
};

export default NewDriverRegistrationDetails;

